import React from 'react';
import { format } from 'date-fns';
import { AlertTriangle, ChevronDown, ChevronUp, Flame, Map, Users, Info, Calendar, Target, Flag } from 'lucide-react';
import { useWeather } from '../../context';

interface WildfireDetailsProps {
  fires: any[];
}

export function WildfireDetails({ fires }: WildfireDetailsProps) {
  const { selectedReport, setSelectedReport } = useWeather();

  const totalArea = fires.reduce((sum, fire) => sum + (fire.report?.areaAC || 0), 0);
  const averageContainment = fires.reduce((sum, fire) => sum + (fire.report?.perContained || 0), 0) / fires.length;
  const totalPersonnel = fires.reduce((sum, fire) => sum + (fire.report?.personnel || 0), 0);

  // Sort fires by discovery date (most recent first)
  const sortedFires = [...fires].sort((a, b) => {
    const dateA = new Date(a.report?.startDateISO || 0).getTime();
    const dateB = new Date(b.report?.startDateISO || 0).getTime();
    return dateB - dateA;
  });

  const formatDate = (timestamp: number | string | null) => {
    if (!timestamp) return 'Unknown';
    try {
      // Handle NIFC timestamps (milliseconds since epoch)
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp.toString().length === 10 ? timestamp * 1000 : timestamp)
        : new Date(timestamp);
      return format(date, 'PPp');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatCost = (cost: number | null) => {
    if (!cost) return 'Unknown';
    return `$${cost.toLocaleString()}`;
  };

  return (
    <div className="h-full overflow-auto">
      <div className="mb-6 grid grid-cols-4 gap-4">
        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-5 h-5 text-[var(--danger-color)]" />
            <h4 className="font-semibold">Active Fires</h4>
          </div>
          <div className="text-2xl font-bold">{fires.length}</div>
        </div>
        
        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <Map className="w-5 h-5 text-[var(--accent-color)]" />
            <h4 className="font-semibold">Total Area</h4>
          </div>
          <div className="text-2xl font-bold">{totalArea.toLocaleString()} acres</div>
        </div>
        
        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-[var(--warning-color)]" />
            <h4 className="font-semibold">Avg Containment</h4>
          </div>
          <div className="text-2xl font-bold">{averageContainment.toFixed(1)}%</div>
        </div>

        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-[var(--accent-color)]" />
            <h4 className="font-semibold">Total Personnel</h4>
          </div>
          <div className="text-2xl font-bold">{totalPersonnel.toLocaleString()}</div>
        </div>
      </div>

      <div className="space-y-4">
        {sortedFires.map((fire) => {
          const containment = fire.report?.perContained || 0;
          const containmentColor = containment < 30 ? 'var(--danger-color)' : 
                                 containment < 70 ? 'var(--warning-color)' : 
                                 'var(--success-color)';
          const isSelected = selectedReport?.id === fire.id;
          const attributes = fire.report?.attributes || {};
          
          return (
            <div 
              key={fire.id}
              className={`glass-panel p-4 transition-all duration-300 ${
                isSelected ? 'ring-2 ring-[var(--accent-color)]' : ''
              }`}
              ref={isSelected ? (el) => {
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              } : undefined}
            >
              <div 
                className="flex justify-between items-start cursor-pointer"
                onClick={() => setSelectedReport(isSelected ? null : fire)}
              >
                <div>
                  <h4 className="text-lg font-semibold flex items-center gap-2">
                    {fire.report?.name || 'Unnamed Fire'}
                    <span className="text-sm text-[var(--secondary-text)]">
                      {fire.report?.POOState ? `US-${fire.report.POOState}` : ''}
                    </span>
                    {isSelected ? 
                      <ChevronUp className="w-4 h-4 text-[var(--secondary-text)]" /> : 
                      <ChevronDown className="w-4 h-4 text-[var(--secondary-text)]" />
                    }
                  </h4>
                  <p className="text-sm text-[var(--secondary-text)]">{fire.locationName}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium" style={{ color: containmentColor }}>
                    {containment}% Contained
                  </div>
                  <div className="text-[var(--secondary-text)] text-sm">
                    {fire.report?.areaAC?.toLocaleString()} acres
                  </div>
                </div>
              </div>

              {isSelected && (
                <div className="mt-4 space-y-4 border-t border-[var(--border-color)] pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">Discovery:</span>
                        <span>{formatDate(attributes.FireDiscoveryDateTime)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">Last Update:</span>
                        <span>{formatDate(attributes.ModifiedOnDateTime_dt)}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Flag className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">FIPS:</span>
                        <span>{attributes.POOFips || 'Unknown'}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">Personnel:</span>
                        <span>{attributes.TotalIncidentPersonnel?.toLocaleString() || 'Unknown'}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">Cost:</span>
                        <span>{formatCost(attributes.EstimatedCostToDate)}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-[var(--accent-color)]" />
                        <span className="text-[var(--secondary-text)]">Report Time:</span>
                        <span>{formatDate(attributes.ICS209ReportDateTime)}</span>
                      </div>
                    </div>
                  </div>

                  {attributes.FireBehaviorGeneral && (
                    <div>
                      <h5 className="font-semibold mb-2">Fire Behavior</h5>
                      <p className="text-sm">{attributes.FireBehaviorGeneral}</p>
                    </div>
                  )}

                  {attributes.PrimaryFuelModel && (
                    <div>
                      <h5 className="font-semibold mb-2">Fuel Model</h5>
                      <p className="text-sm">{attributes.PrimaryFuelModel}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}